<div class="flex items-center justify-center dialog-header">
    {{ isEditMode ? 'Edit' : 'Create' }} Feature

    <div class="absolute right-2">
        <button (click)="handleCloseDialog()" class="outline-none p-5">
            <i class="pi pi-times"></i>
        </button>
    </div>
</div>

<div class="dialog-content">
    <form [formGroup]="featureForm" (ngSubmit)="submitForm()" class="p-fluid grid grid-cols-2 gap-6">

        <!-- Feature Key -->
        <div class="flex flex-col">
            <label for="feature_key" class="mb-2 font-semibold">Feature Key *</label>
            <input pInputText id="feature_key" formControlName="feature_key"
                placeholder="e.g., lead, advanced_lead_management"
                class="w-full border rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
            <div *ngIf="featureForm.get('feature_key')?.invalid && (featureForm.get('feature_key')?.touched || submitted)"
                class="text-red-500 text-xs mt-1">
                Feature Key is required.
            </div>
        </div>

        <!-- Label -->
        <div class="flex flex-col">
            <label for="label" class="mb-2 font-semibold">Label *</label>
            <input pInputText id="label" formControlName="label"
                placeholder="Display name"
                class="w-full border rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
            <div *ngIf="featureForm.get('label')?.invalid && (featureForm.get('label')?.touched || submitted)"
                class="text-red-500 text-xs mt-1">
                Label is required.
            </div>
        </div>

        <!-- Category (Fixed/Limit Type) -->
        <div class="flex flex-col">
            <label for="category" class="mb-2 font-semibold">Category *</label>
            <p-select id="category" [options]="categoryOptions" formControlName="category" appendTo="body"
                placeholder="Select category" class="w-full" />
            <div *ngIf="featureForm.get('category')?.invalid && (featureForm.get('category')?.touched || submitted)"
                class="text-red-500 text-xs mt-1">
                Category is required.
            </div>
            <small class="text-gray-500 mt-1">Fixed features or Limit features</small>
        </div>

        <!-- Feature Level - Only for Fixed category -->
        <div class="flex flex-col" *ngIf="featureForm.get('category')?.value === 'fixed'">
            <label for="feature_level" class="mb-2 font-semibold">Feature Level *</label>
            <p-select id="feature_level" [options]="featureLevelOptions" formControlName="feature_level" appendTo="body"
                placeholder="Select feature level" class="w-full" />
            <div *ngIf="featureForm.get('feature_level')?.invalid && (featureForm.get('feature_level')?.touched || submitted)"
                class="text-red-500 text-xs mt-1">
                Feature Level is required.
            </div>
            <small class="text-gray-500 mt-1">Hierarchy: Module → Feature → Sub-feature</small>
        </div>

        <!-- Parent Feature Key - Only for Fixed category -->
        <div class="flex flex-col" *ngIf="featureForm.get('category')?.value === 'fixed'">
            <label for="parent_feature_key" class="mb-2 font-semibold">Parent Feature</label>
            <p-select id="parent_feature_key" [options]="parentFeatureOptions" formControlName="parent_feature_key" 
                appendTo="body" placeholder="Select parent feature (optional)" class="w-full" 
                [showClear]="true" />
            <small class="text-gray-500 mt-1">Optional: Specify parent for hierarchical features</small>
        </div>

        <!-- Unit Type - Only for Limit category -->
        <div class="flex flex-col col-span-2" *ngIf="featureForm.get('category')?.value === 'limit'">
            <label for="unit_type" class="mb-2 font-semibold">Unit Type</label>
            <p-select id="unit_type" [options]="unitTypeOptions" formControlName="unit_type" appendTo="body"
                placeholder="Select unit type (optional)" class="w-full" showClear="true"
                optionLabel="label" optionValue="value" />
            <small class="text-gray-500 mt-1">Optional: Specify the unit of measurement for this limit (e.g., users, emails, GB)</small>
        </div>

        <!-- Status -->
        <div class="flex flex-col">
            <label for="status" class="mb-2 font-semibold">Status *</label>
            <p-select id="status" [options]="statusOptions" formControlName="status" placeholder="Select a status"
                class="w-full" />
            <div *ngIf="featureForm.get('status')?.invalid && (featureForm.get('status')?.touched || submitted)"
                class="text-red-500 text-xs mt-1">
                Status is required.
            </div>
        </div>
            <div class="flex flex-col" >
                <label for="packages_id" class="mb-2 font-semibold">Applicable Packages</label>
                <p-multiSelect 
                    id="packages_id" 
                    [options]="packageOptions" 
                    formControlName="packages_id" 
                    optionLabel="label" 
                    optionValue="value"
                    placeholder="Select packages (optional)" 
                    class="w-full"
                    [showClear]="true"
                    appendTo="body"
                    display="chip">
                </p-multiSelect>
                <small class="text-gray-500 mt-1">Optional: Select which packages include this feature</small>
        </div>
        <!-- Actions - Multi-select - Only for Fixed category -->
        <div class="flex flex-col col-span-2" *ngIf="featureForm.get('category')?.value === 'fixed'">
            <label for="actions" class="mb-2 font-semibold">Actions</label>
            <p-multiSelect 
                id="actions" 
                [options]="actionOptions" 
                formControlName="actions" 
                optionLabel="label" 
                optionValue="value"
                placeholder="Select actions (optional)" 
                class="w-full"
                [showClear]="true"
                appendTo="body"
                display="chip">
                <ng-template let-action pTemplate="item">
                    <div class="flex items-center gap-2">
                        <i class="pi" [ngClass]="action.icon"></i>
                        <span>{{ action.label }}</span>
                    </div>
                </ng-template>
            </p-multiSelect>
            <small class="text-gray-500 mt-1">Optional: Select which actions are available for this feature</small>
        </div>

        <!-- Description -->
        <div class="flex flex-col col-span-2">
            <label for="description" class="mb-2 font-semibold">Description</label>
            <textarea pTextarea id="description" formControlName="description" rows="3"
                placeholder="Detailed description of this feature"
                class="w-full border rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500"></textarea>
        </div>

        <!-- Submit Button -->
        <div class="col-span-2 flex justify-end mt-6">
            <p-button [label]="isEditMode ? 'Update' : 'Create'" type="submit" [loading]="isLoading" icon="pi pi-check"
                class="w-full" [disabled]="featureForm.invalid || isLoading"></p-button>
        </div>

    </form>
</div>